import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { captureException } from '@sentry/nextjs';
import {
  Collapse,
  Divider,
  Grid,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';

import { GenericDeleteModal } from '@/domains/Modals/GenericDeleteModal/GenericDeleteModal';
import { GenericFailureModal } from '@/domains/Modals/GenericFailureModal/GenericFailureModal';
import { GenericLoadingModal } from '@/domains/Modals/GenericLoadingModal/GenericLoadingModal';

import genericModalStore from '@/stores/GenericModalStore';

import { dateDiff, capitalize } from '@/utils/index';
import { Chip } from '@/components/Chip/Chip';
import { CommentsProvider } from '@/components/Comments/CommentContext';
import CommentForm from '@/components/Comments/CommentForm/CommentForm';
import CommentModals from '@/components/Comments/CommentModal/CommentModals';
import CustomAvatar from '@/components/CustomAvatar';
import { DeleteIcon } from '@/components/icons';
import { CommentIcon } from '@/components/icons/CommentIcon';
import LoadingIndicator from '@/components/LoadingIndicator';

import xw from 'xwind';
import {
  DesktopAvatarWrapper,
  Wrapper,
  MobileAvatarWrapper,
  MessageIcon,
  CommentSumary,
} from './PostBase.styles';
import { PostBasePropTypes } from './PostBase.types';
import CommentList from '../Comments/CommentList/CommentList';

function getCommentCountMessage(commentSize: number): string {
  if (!commentSize) return 'Be the first to comment!';
  if (commentSize === 1) return '1 Comment';
  return `${commentSize} Comments`;
}

export function PostBase({
  user,
  commentProps,
  link,
  publication,
  chipProps,
  children,
  id,
}: PostBasePropTypes): JSX.Element {
  const postRef = useRef<HTMLDivElement | null>(null);
  const scrolledRef = useRef(false);
  const [loadingDeletion, setLoadingDeletion] = useState(false);
  const [commentsSize, setCommentSize] = useState(
    commentProps?.comments?.length || 0,
  );

  const title = publication.title ? capitalize(publication.title) : '';

  async function handlePostDeletion(): Promise<void> {
    setLoadingDeletion(true);

    genericModalStore?.updateModal({
      isOpen: true,
      children: <GenericLoadingModal />,
    });
    try {
      await publication?.onDelete?.(publication?.id);
      genericModalStore.handleClose();
    } catch (error) {
      captureException(error);
      genericModalStore?.updateModal({
        isOpen: true,
        children: (
          <GenericFailureModal onCancel={genericModalStore.handleClose} />
        ),
      });
    } finally {
      setLoadingDeletion(false);
    }
  }

  function handleNewComment() {
    setCommentSize((prevCommentSize) => prevCommentSize + 1);
  }

  function handleCommentDeletion() {
    setCommentSize((prevCommentSize) => prevCommentSize - 1);
  }

  function handleOpenModalDeletion() {
    genericModalStore.updateModal({
      isOpen: true,
      children: (
        <GenericDeleteModal
          onCancelHandler={genericModalStore.handleClose}
          onDeleteHandler={handlePostDeletion}
        />
      ),
    });
  }

  useEffect(() => {
    if (
      process.browser &&
      postRef.current &&
      window.location.hash.substr(1) === id &&
      !scrolledRef.current
    ) {
      scrolledRef.current = true;
      postRef.current.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
    }
  }, [id]);

  return (
    <Grid
      ref={postRef}
      container
      display="grid"
      gridTemplateColumns={(theme) => ({
        xs: '1fr',
        sm: `${theme.spacing(20)} 1fr`,
      })}
      gap={{ sm: 6 }}
      css={xw`mb-10`}
    >
      <DesktopAvatarWrapper
        container
        flexDirection="column"
        gap={2}
        justifyContent="flex-start"
        alignItems="center"
      >
        <CustomAvatar
          size={{ desktop: 20, mobile: 0 }}
          fontSize={{ desktop: 10, mobile: 10 }}
          name={user?.firstName}
          image={user?.image?.url}
        />
        {chipProps && <Chip {...chipProps} size="small" color="primary" />}
      </DesktopAvatarWrapper>
      <Grid container gap={5} flexDirection="column">
        <Wrapper hasComments={commentProps?.comments?.length} elevation={0}>
          <Grid container flexDirection="column" gap={6} minHeight="inherit">
            <Typography variant="h2" component="span" color="primary.500">
              {title}
            </Typography>
            <Grid
              container
              item
              display="grid"
              gap={2}
              gridTemplateColumns={{
                xs: `auto 1fr 32px`,
                sm: `1fr 32px`,
              }}
            >
              <MobileAvatarWrapper
                item
                gridColumn="span 1"
                gridRow="span 2"
                container
                gap={3}
                alignItems="center"
                flexDirection="column"
              >
                <CustomAvatar
                  size={{ desktop: 0, mobile: 12 }}
                  fontSize={{ desktop: 0, mobile: 5 }}
                  name={user?.firstName}
                  image={user?.image?.url}
                />
                {chipProps && (
                  <Chip {...chipProps} size="small" color="primary" />
                )}
              </MobileAvatarWrapper>
              <Grid item container flexDirection={{ xs: 'column', sm: 'row' }}>
                <Grid item container gap={2} width="fit-content">
                  <Typography variant="body1" fontWeight={{ xs: 700, sm: 700 }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  {link && (
                    <>
                      <Typography variant="body1">posted in</Typography>
                      <Link href={link.url} passHref>
                        <Typography
                          variant="body1"
                          fontWeight={{ xs: 700, sm: 700 }}
                          variantMapping={{ body1: 'a' }}
                          color="primary.500"
                        >
                          {link.label}
                        </Typography>
                      </Link>
                    </>
                  )}
                </Grid>
                {(publication?.createdAt || publication?.updatedAt) && (
                  <>
                    <DesktopAvatarWrapper item>
                      <Typography variant="body1" color="grey.300">
                        {' '}
                        ・{' '}
                      </Typography>
                    </DesktopAvatarWrapper>
                    <Grid item alignSelf="flex-start">
                      <Typography variant="body1" color="grey.300">
                        {dateDiff(
                          publication?.updatedAt || publication?.createdAt,
                        )}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
              {publication?.deletable && (
                <Box marginLeft="auto">
                  <IconButton
                    onClick={handleOpenModalDeletion}
                    size="small"
                    component="button"
                    aria-label="delete post"
                  >
                    {loadingDeletion ? (
                      <LoadingIndicator />
                    ) : (
                      <SvgIcon fontSize="small">
                        <DeleteIcon width="1.5rem" height="1.5rem" />
                      </SvgIcon>
                    )}
                  </IconButton>
                </Box>
              )}
            </Grid>
            <Grid item flexGrow={1} width={1} display="block">
              {children}
            </Grid>
            {commentProps?.show && (
              <Grid item width={1} flexShrink={1}>
                <Divider variant="fullWidth" />
                <CommentSumary
                  {...(commentProps?.collapsible
                    ? {
                        onClick: () =>
                          commentProps?.onToggleCollapse?.(
                            commentProps?.collapsed,
                            publication?.id,
                          ),
                        type: 'button',
                        as: 'button',
                      }
                    : null)}
                >
                  <Grid container alignItems="baseline" gap={2} height={60}>
                    <Box paddingTop={{ xs: 2, sm: 5 }}>
                      <MessageIcon
                        collapsed={
                          !commentProps?.collapsible || commentProps?.collapsed
                        }
                      >
                        <CommentIcon />
                      </MessageIcon>
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight={{ xs: 'bold', sm: 'bold' }}
                      color={
                        commentProps?.collapsed
                          ? 'primary.main'
                          : 'SecondaryText'
                      }
                      paddingTop={{ xs: 2, sm: 5 }}
                    >
                      {getCommentCountMessage(commentsSize)}
                    </Typography>
                  </Grid>
                </CommentSumary>
              </Grid>
            )}
          </Grid>
        </Wrapper>
        {commentProps?.show && (
          <Collapse
            in={!(commentProps?.collapsible && commentProps?.collapsed)}
          >
            <div>
              <CommentsProvider
                comments={commentProps?.comments}
                contentId={publication?.id}
                onComment={handleNewComment}
                onDelete={handleCommentDeletion}
              >
                <CommentForm grayInput />
                <Divider variant="fullWidth" />
                <CommentList grayInput />
                <CommentModals />
              </CommentsProvider>
            </div>
          </Collapse>
        )}
      </Grid>
    </Grid>
  );
}
